/**
 * EntryView - Entry screen wrapper component
 *
 * Renders the EntryScreen + UserAuthModal for the entry/landing view.
 * Pure presentational — all state/handlers come via props.
 *
 * Extracted from App.tsx lines 989–1023
 */

import { EntryScreen } from "../components/EntryScreen";
import { UserAuthModal } from "../components/UserAuthModal";

import type { useSupabaseAuth } from "../hooks/useSupabaseAuth";

interface EntryViewProps {
  // User info
  userDisplayName: string;
  userAvatarUrl: string | null;
  userInitials: string | undefined;
  // Entry screen actions
  onUserClick: () => void;
  onWeaveIdea: (
    prompt: string,
    initialImageUrls?: string[],
    initialFiles?: import("../components/AttachFile").AttachedFile[],
  ) => void;
  onDrawDirect: (
    prompt: string,
    initialImageUrls?: string[],
    initialFiles?: import("../components/AttachFile").AttachedFile[],
  ) => void;
  onStartCanvas: () => void;
  onSelectProject: (id: string) => void;
  // Data
  recentDiagrams: any[];
  isLoadingRecent: boolean;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isDark: boolean;
  // Conversation mode
  isConversationActive?: boolean;
  conversationTopic?: string;
  conversationInitialImageUrls?: string[];
  conversationInitialFiles?: import("../components/AttachFile").AttachedFile[];
  onBackFromConversation?: () => void;
  onGoToDraw?: (
    context: import("../components/ConversationContent").ConversationContextForDraw,
  ) => void;
  userId?: string | null;
  // Auth modal
  isAuthModalOpen: boolean;
  onCloseAuthModal: () => void;
  auth: ReturnType<typeof useSupabaseAuth>;
}

export const EntryView: React.FC<EntryViewProps> = ({
  userDisplayName,
  userAvatarUrl,
  userInitials,
  onUserClick,
  onWeaveIdea,
  onDrawDirect,
  onStartCanvas,
  onSelectProject,
  recentDiagrams,
  isLoadingRecent,
  isAuthenticated,
  isAuthLoading,
  isDark,
  isConversationActive,
  conversationTopic,
  conversationInitialImageUrls,
  conversationInitialFiles,
  onBackFromConversation,
  onGoToDraw,
  userId,
  isAuthModalOpen,
  onCloseAuthModal,
  auth,
}) => {
  return (
    <div style={{ height: "100%" }} className="ideaweave-app">
      <EntryScreen
        userName={userDisplayName}
        userAvatarUrl={userAvatarUrl}
        userInitials={userInitials}
        onUserClick={onUserClick}
        onWeaveIdea={onWeaveIdea}
        onDrawDirect={onDrawDirect}
        onStartCanvas={onStartCanvas}
        recentDiagrams={recentDiagrams}
        onSelectProject={onSelectProject}
        isLoadingRecent={isLoadingRecent}
        isAuthenticated={isAuthenticated}
        isAuthLoading={isAuthLoading}
        isDark={isDark}
        isConversationActive={isConversationActive}
        conversationTopic={conversationTopic}
        conversationInitialImageUrls={conversationInitialImageUrls}
        conversationInitialFiles={conversationInitialFiles}
        onBackFromConversation={onBackFromConversation}
        onGoToDraw={onGoToDraw}
        userId={userId}
        onOpenAuthModal={onUserClick}
      />
      <UserAuthModal
        isOpen={isAuthModalOpen}
        onClose={onCloseAuthModal}
        session={auth.session}
        profile={auth.profile}
        loading={auth.loading}
        profileLoading={auth.profileLoading}
        error={auth.error}
        onClearError={() => auth.setError(null)}
        onGoogleSignIn={auth.signInWithGoogle}
        onSendOtp={auth.sendOtp}
        onVerifyOtp={auth.verifyOtp}
        onProfileUpdate={auth.updateProfile}
        onSignOut={auth.signOut}
      />
    </div>
  );
};
